<template>
  <div>
    <div class="back-row">
      <button class="btn-back" @click="$router.push('/procurement/purchase-orders')">
        <font-awesome-icon icon="arrow-left" /> Back to Purchase Orders
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div><p>Loading...</p>
    </div>

    <template v-else-if="po">
      <div class="page-header">
        <div>
          <div class="title-row">
            <h5 class="page-title">{{ po.title }}</h5>
            <span :class="['status-badge', `status-badge--${po.status}`]">{{ formatStatus(po.status) }}</span>
          </div>
          <p class="page-sub">
            <span class="mono">{{ po.po_number }}</span>
            <span v-if="po.pr"> &nbsp;·&nbsp; From PR <span class="mono-inline">{{ po.pr.pr_number }}</span></span>
          </p>
        </div>
        <div class="header-actions">
          <button class="btn-outline" @click="$router.push(`/procurement/purchase-orders/${po.id}/print`)">
            <font-awesome-icon icon="print" /> Print
          </button>
          <button v-if="po.status === 'draft'" class="btn-outline" @click="$router.push(`/procurement/purchase-orders/${po.id}/edit`)">
            <font-awesome-icon icon="edit" /> Edit
          </button>
          <button
            v-if="['issued','partially_received','completed'].includes(po.status)"
            class="btn-attach"
            @click="attachModal.open = true">
            <font-awesome-icon icon="paperclip" /> Attach Documents
          </button>
          <button v-if="po.status === 'draft'" class="btn-issue" :disabled="actioning" @click="issuePO">
            <span v-if="actioning === 'issue'" class="spinner-sm"></span>
            <template v-if="actioning === 'issue'">Submitting...</template>
            <template v-else>Issue Order</template>
          </button>
          <button v-if="!['completed','cancelled'].includes(po.status)" class="btn-cancel" :disabled="actioning" @click="cancelPO">
            <span v-if="actioning === 'cancel'" class="spinner-sm"></span>
            Cancel
          </button>
        </div>
      </div>

      <!-- Meta grid -->
      <div class="meta-grid">
        <div class="meta-card">
          <span class="meta-label">Vendor</span>
          <span class="meta-value">{{ po.vendor?.name || '—' }}</span>
        </div>
        <div class="meta-card" v-if="po.project">
          <span class="meta-label">Project</span>
          <span class="meta-value">{{ po.project.project_code }} — {{ po.project.project_name }}</span>
        </div>
        <div class="meta-card" v-if="po.department">
          <span class="meta-label">Department</span>
          <span class="meta-value">{{ po.department.name }}</span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Currency</span>
          <span class="meta-value">{{ po.currency }}</span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Expected Delivery</span>
          <span class="meta-value">{{ po.expected_delivery_date ? formatDate(po.expected_delivery_date) : '—' }}</span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Payment Terms</span>
          <span class="meta-value">{{ po.payment_terms || '—' }}</span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Payment Status</span>
          <span class="status-chip" :class="`status-pay-${po.payment_status || 'unpaid'}`">
            {{ { unpaid:'Unpaid', partially_paid:'Partially Paid', fully_paid:'Fully Paid', overpaid:'Overpaid' }[po.payment_status] || 'Unpaid' }}
          </span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Issued At</span>
          <span class="meta-value">{{ po.issued_at ? formatDate(po.issued_at) : '—' }}</span>
        </div>
        <div class="meta-card" v-if="po.location">
          <span class="meta-label">Destination Location</span>
          <span class="meta-value">
            <font-awesome-icon icon="map-marker-alt" class="me-1" style="color:var(--clr-primary,#4f46e5)" />{{ po.location.store_name }}
          </span>
        </div>
        <div class="meta-card">
          <span class="meta-label">Delivery Address</span>
          <span class="meta-value">{{ po.delivery_address || '—' }}</span>
        </div>
      </div>

      <!-- Line items -->
      <div class="section-card">
        <h6 class="section-title">Line Items</h6>
        <div class="table-wrap">
          <table class="detail-table">
            <colgroup>
              <col style="width:4%">
              <col style="width:34%">
              <col style="width:16%">
              <col style="width:8%">
              <col style="width:8%">
              <col style="width:14%">
              <col style="width:16%">
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>Specification</th>
                <th class="num-col">Qty</th>
                <th>UOM</th>
                <th class="num-col">Unit Price</th>
                <th class="num-col">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, i) in po.lines" :key="line.id">
                <td class="text-muted">{{ i + 1 }}</td>
                <td>{{ line.description }}</td>
                <td class="text-muted">{{ line.specification || '—' }}</td>
                <td class="num-col">{{ line.quantity }}</td>
                <td>{{ line.uom || '—' }}</td>
                <td class="num-col">{{ formatNum(line.unit_price) }}</td>
                <td class="num-col fw-bold">{{ formatNum(line.total) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="tfoot-sub">
                <td colspan="5"></td>
                <td class="tfoot-label">Subtotal</td>
                <td class="num-col">{{ formatNum(po.subtotal) }}</td>
              </tr>
              <tr class="tfoot-sub" v-if="po.tax_amount">
                <td colspan="5"></td>
                <td class="tfoot-label">Tax / VAT</td>
                <td class="num-col">{{ formatNum(po.tax_amount) }}</td>
              </tr>
              <tr class="tfoot-total">
                <td colspan="5"></td>
                <td class="tfoot-label">Total</td>
                <td class="num-col fw-bold total-val">{{ formatNum(po.total) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Notes -->
      <div class="section-card" v-if="po.notes || po.description">
        <h6 class="section-title">Notes</h6>
        <div class="notes-text" v-if="po.description"><strong>Instructions:</strong> <span class="rich-text" v-html="po.description"></span></div>
        <p class="notes-text" v-if="po.notes"><strong>Internal Notes:</strong> {{ po.notes }}</p>
      </div>

      <!-- Supply Items -->
      <div class="section-card" v-if="['issued','partially_received','completed'].includes(po.status) && canSupply">
        <div class="supply-header">
          <h6 class="section-title" style="margin:0">{{ isGoodsPO ? 'Supply Items' : 'Delivery Status' }}</h6>
          <button
            v-if="['issued','partially_received'].includes(po.status) && hasUnsuppliedLines"
            class="btn-supply-all"
            :disabled="!!actioning"
            @click="openSupplyAllModal">
            <font-awesome-icon :icon="isGoodsPO ? 'boxes' : 'check-double'" class="me-1" />
            {{ isGoodsPO ? 'Supply All' : 'Mark All Delivered' }}
          </button>
        </div>

        <div class="supply-table-wrap">
          <table class="supply-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <template v-if="isGoodsPO">
                  <th>Product</th>
                  <th>Location</th>
                  <th class="num-col">Ordered</th>
                  <th class="num-col">Supplied</th>
                  <th class="num-col">Remaining</th>
                </template>
                <th>Status</th>
                <th v-if="['issued','partially_received'].includes(po.status)"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, i) in po.lines" :key="line.id">
                <td class="text-muted">{{ i + 1 }}</td>
                <td>{{ line.description }}</td>
                <template v-if="isGoodsPO">
                  <td>
                    <span v-if="resolvedProduct(line)" class="supply-product-tag">{{ resolvedProduct(line).product_name }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="resolvedLocation(line)" class="supply-location-tag">
                      <font-awesome-icon icon="map-marker-alt" class="me-1" />{{ resolvedLocation(line).store_name }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="num-col">{{ line.quantity }}</td>
                  <td class="num-col supply-supplied-qty">{{ line.supplied_quantity || 0 }}</td>
                  <td class="num-col" :class="{ 'supply-remaining-warn': supplyRemaining(line) > 0 }">
                    {{ supplyRemaining(line) }}
                  </td>
                </template>
                <td>
                  <span :class="['supply-badge', `supply-badge--${line.supply_status || 'pending'}`]">
                    {{ supplyStatusLabel(line.supply_status) }}
                  </span>
                </td>
                <td v-if="['issued','partially_received'].includes(po.status)">
                  <button
                    v-if="line.supply_status !== 'supplied'"
                    class="btn-supply-item"
                    @click="openSupplyItemModal(line)">
                    {{ isGoodsPO ? 'Supply' : 'Mark Delivered' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Approval -->
      <div class="section-card" v-if="po.approval_request">
        <h6 class="section-title">Approval</h6>
        <div class="approval-status-row">
          <span :class="['appr-badge', `appr-badge--${po.approval_request.status}`]">{{ formatApprovalStatus(po.approval_request.status) }}</span>
          <span class="appr-step">Step {{ po.approval_request.current_step }}</span>
        </div>
        <div class="approval-steps" v-if="po.approval_request.steps?.length">
          <div v-for="step in po.approval_request.steps" :key="step.id" class="appr-step-row">
            <div :class="['appr-dot', `appr-dot--${step.status}`]"></div>
            <div class="appr-step-info">
              <div class="appr-step-name">Step {{ step.step_order }}: {{ step.position_name || 'Approver' }}</div>
              <div class="appr-step-meta">
                <span :class="['appr-step-status', `appr-step-status--${step.status}`]">{{ capitalise(step.status) }}</span>
                <span v-if="step.acted_at" class="appr-step-date"> · {{ formatDate(step.acted_at) }}</span>
                <p v-if="step.comments" class="appr-step-comment">{{ step.comments }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section-card" v-else-if="po.status === 'pending_approval'">
        <h6 class="section-title">Approval</h6>
        <p class="text-muted-sm">Submitted for approval — loading workflow details...</p>
      </div>
    <!-- GRN Section -->
    <div class="section-card" v-if="po.grns && po.grns.length">
      <h6 class="section-title">Goods Receipt Notes (GRNs)</h6>
      <div class="supply-table-wrap">
        <table class="supply-table">
          <thead>
            <tr>
              <th>#</th>
              <th>GRN No.</th>
              <th>Item</th>
              <th>Product</th>
              <th>Location</th>
              <th class="num-col">Qty Received</th>
              <th>Date Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="grn in po.grns" :key="grn.id">
              <tr v-for="(line, li) in grn.lines" :key="line.id">
                <td class="text-muted">{{ li + 1 }}</td>
                <td>
                  <span class="grn-number-badge">{{ grn.grn_number }}</span>
                </td>
                <td>{{ line.description || '—' }}</td>
                <td>
                  <span v-if="line.product" class="supply-product-tag">{{ line.product.product_name }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span v-if="line.location" class="supply-location-tag">
                    <font-awesome-icon icon="map-marker-alt" class="me-1" />{{ line.location.store_name }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="num-col supply-supplied-qty">{{ line.quantity_received }}</td>
                <td>{{ formatDate(grn.received_at) }}</td>
                <td class="grn-action-col">
                  <button
                    v-if="li === 0"
                    class="grn-print-btn"
                    title="Print GRN"
                    @click="$router.push(`/procurement/grns/${grn.id}/print`)">
                    <font-awesome-icon icon="print" />
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
      <!-- Quantity Amendment Log -->
      <div class="section-card" v-if="quantityLogs.length">
        <h6 class="section-title">Quantity Amendment History</h6>
        <div class="qlog-list">
          <div v-for="(log, i) in quantityLogs" :key="i" class="qlog-row">
            <div class="qlog-icon">
              <font-awesome-icon icon="exchange-alt" />
            </div>
            <div class="qlog-body">
              <div class="qlog-desc">
                <span class="qlog-line-name">{{ log.lineName }}</span>
                <span class="qlog-change">
                  <span class="qlog-old">{{ formatNum(log.old_quantity) }}</span>
                  <font-awesome-icon icon="arrow-right" class="qlog-arrow" />
                  <span class="qlog-new">{{ formatNum(log.new_quantity) }}</span>
                  <span v-if="log.uom" class="qlog-uom">{{ log.uom }}</span>
                </span>
              </div>
              <div class="qlog-meta">
                <span class="qlog-by">{{ log.changed_by_name }}</span>
                <span v-if="log.changed_by_designation" class="qlog-desig"> · {{ log.changed_by_designation }}</span>
                <span class="qlog-sep"> · </span>
                <span class="qlog-step">Step {{ log.step_order }} approval</span>
                <span class="qlog-sep"> · </span>
                <span class="qlog-date">{{ formatDate(log.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div class="section-card" v-if="po.documents && po.documents.length">
        <h6 class="section-title">Attached Documents</h6>
        <div class="doc-list">
          <div v-for="doc in po.documents" :key="doc.id" class="doc-row">
            <div class="doc-icon">
              <font-awesome-icon :icon="docIcon(doc.file_name)" />
            </div>
            <div class="doc-info">
              <div class="doc-name">{{ doc.document_name }}</div>
              <div class="doc-meta">{{ doc.file_name }} <span v-if="doc.file_size">· {{ formatFileSize(doc.file_size) }}</span></div>
            </div>
            <div class="doc-actions">
              <a :href="doc.file_url" target="_blank" class="btn-doc-view">
                <font-awesome-icon icon="eye" /> View
              </a>
              <button class="btn-doc-delete" @click="confirmDeleteDoc(doc)" :disabled="!!actioning">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>
        </div>
      </div>

    <!-- Payments Section -->
    <div class="section-card" v-if="!['draft','cancelled'].includes(po.status)">
      <div class="section-title-row">
        <h6 class="section-title" style="margin:0">Payments</h6>
        <button v-if="canRecordPayment && po.payment_status !== 'fully_paid'"
                class="btn-pay-record" @click="openPaymentModal">
          + Record Payment
        </button>
      </div>

      <!-- Summary bar -->
      <div class="pay-summary-bar" v-if="paymentData">
        <div class="pay-sum-item">
          <span class="pay-sum-label">PO Total</span>
          <span class="pay-sum-val">{{ po.currency }} {{ formatNum(po.total) }}</span>
        </div>
        <div class="pay-sum-item">
          <span class="pay-sum-label">Paid</span>
          <span class="pay-sum-val pay-sum-paid">{{ po.currency }} {{ formatNum(paymentData.po.paid) }}</span>
        </div>
        <div class="pay-sum-item">
          <span class="pay-sum-label">Outstanding</span>
          <span class="pay-sum-val pay-sum-outstanding">{{ po.currency }} {{ formatNum(paymentData.po.outstanding) }}</span>
        </div>
      </div>
      <div class="pay-summary-bar" v-else-if="!paymentsLoading">
        <div class="pay-sum-item">
          <span class="pay-sum-label">PO Total</span>
          <span class="pay-sum-val">{{ po.currency }} {{ formatNum(po.total) }}</span>
        </div>
        <div class="pay-sum-item">
          <span class="pay-sum-label">Outstanding</span>
          <span class="pay-sum-val pay-sum-outstanding">{{ po.currency }} {{ formatNum(po.total) }}</span>
        </div>
      </div>

      <!-- Payment Schedule -->
      <template v-if="paymentData && paymentData.schedules && paymentData.schedules.length">
        <p class="pay-sub-heading">Payment Schedule</p>
        <div class="supply-table-wrap">
          <table class="supply-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th class="text-right">Amount</th>
                <th class="text-right">Paid</th>
                <th class="text-right">Remaining</th>
                <th>Due Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in paymentData.schedules" :key="s.id">
                <td>{{ s.installment_number }}</td>
                <td>{{ s.description || '—' }}</td>
                <td class="text-right">{{ po.currency }} {{ formatNum(s.amount) }}</td>
                <td class="text-right">{{ po.currency }} {{ formatNum(s.amount_paid) }}</td>
                <td class="text-right">{{ po.currency }} {{ formatNum(s.amount_remaining) }}</td>
                <td>{{ s.due_date ? formatDate(s.due_date) : '—' }}</td>
                <td><span class="sched-status" :class="`sched-${s.status}`">{{ s.status.replace(/_/g,' ') }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Payment History -->
      <template v-if="paymentData && paymentData.payments && paymentData.payments.length">
        <p class="pay-sub-heading" style="margin-top:18px">Payment History</p>
        <div class="supply-table-wrap">
          <table class="supply-table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Date</th>
                <th class="text-right">Amount</th>
                <th>Method</th>
                <th>Transaction Ref</th>
                <th>Status</th>
                <th>Receipts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in paymentData.payments" :key="p.id">
                <td class="font-mono">{{ p.payment_number }}</td>
                <td>{{ formatDate(p.payment_date) }}</td>
                <td class="text-right">{{ po.currency }} {{ formatNum(p.amount) }}</td>
                <td>{{ p.payment_method?.replace(/_/g, ' ') }}</td>
                <td class="text-muted">{{ p.transaction_reference || '—' }}</td>
                <td>
                  <span class="sched-status" :class="`sched-${p.status}`">{{ p.status }}</span>
                </td>
                <td>
                  <template v-if="p.documents && p.documents.length">
                    <div v-for="d in p.documents" :key="d.id" class="receipt-row">
                      <a :href="d.file_url" target="_blank" class="pay-receipt-link">
                        <font-awesome-icon icon="file-alt" /> {{ d.document_name || 'Receipt' }}
                      </a>
                      <a :href="d.file_url" :download="d.file_name || d.document_name || 'receipt'"
                         class="pay-receipt-download" title="Download">
                        <font-awesome-icon icon="download" />
                      </a>
                    </div>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <button v-if="p.status === 'completed' && canRecordPayment"
                          class="btn-doc-delete" title="Reverse payment"
                          @click="confirmReversePayment(p)">
                    <font-awesome-icon icon="undo" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div v-if="paymentsLoading" class="pay-loading">Loading payments…</div>
      <div v-else-if="paymentData && !paymentData.payments.length && !paymentData.schedules.length" class="pay-empty">
        No payments recorded yet.
      </div>
    </div>

    <!-- Record Payment Modal -->
    <div v-if="paymentModal.open" class="confirm-overlay" @click.self="paymentModal.open = false">
      <div class="confirm-box pay-modal-box">
        <div class="confirm-header">
          <h4 class="confirm-title">Record Payment</h4>
          <button class="btn-close-modal" @click="paymentModal.open = false">&times;</button>
        </div>

        <div class="pay-modal-body">
          <div class="pay-modal-summary">
            <span>Outstanding: <strong>{{ po.currency }} {{ formatNum(paymentData?.po?.outstanding ?? po.total) }}</strong></span>
          </div>

          <div class="field-grid-2">
            <div class="field-group">
              <label class="field-label">Payment Date <span class="req">*</span></label>
              <input v-model="paymentModal.form.payment_date" type="date" class="supply-form-input" />
            </div>
            <div class="field-group">
              <label class="field-label">Amount <span class="req">*</span></label>
              <input v-model.number="paymentModal.form.amount" type="number" min="0.01" step="0.01"
                     class="supply-form-input" placeholder="0.00" />
            </div>
            <div class="field-group">
              <label class="field-label">Payment Method</label>
              <select v-model="paymentModal.form.payment_method" class="supply-form-input">
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="online">Online</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Transaction Reference</label>
              <input v-model="paymentModal.form.transaction_reference" class="supply-form-input" placeholder="e.g. TXN-12345" />
            </div>
            <div class="field-group">
              <label class="field-label">Payment Reference</label>
              <input v-model="paymentModal.form.payment_reference" class="supply-form-input" placeholder="e.g. CHQ-00012" />
            </div>
            <div class="field-group">
              <label class="field-label">Notes</label>
              <input v-model="paymentModal.form.notes" class="supply-form-input" placeholder="Optional notes" />
            </div>
            <div class="field-group field-span-2">
              <label class="field-label">Attach Receipt <span class="req">*</span></label>
              <div class="doc-drop-zone" :class="{ 'doc-drop-zone--filled': paymentModal.form.file }"
                   @click="$refs.payFileInput.click()"
                   @dragover.prevent @drop.prevent="onPayFileDrop">
                <template v-if="paymentModal.form.file">
                  <font-awesome-icon icon="file-alt" style="color:#16a34a;font-size:16px;flex-shrink:0" />
                  <span style="flex:1;text-align:left;word-break:break-all">{{ paymentModal.form.file.name }}</span>
                  <button type="button" style="background:none;border:none;color:#9ca3af;cursor:pointer;padding:0 2px"
                          @click.stop="paymentModal.form.file = null; paymentModal.form.document_name = ''; $refs.payFileInput.value = ''">
                    <font-awesome-icon icon="times" />
                  </button>
                </template>
                <template v-else>
                  <font-awesome-icon icon="upload" />
                  <span>Click or drag a receipt file here</span>
                </template>
              </div>
              <input ref="payFileInput" type="file" style="display:none"
                     accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                     @change="onPayFileSelect" />
              <input v-if="paymentModal.form.file"
                     v-model="paymentModal.form.document_name" class="supply-form-input"
                     style="margin-top:6px" placeholder="Document name (e.g. Payment Receipt)" />
            </div>
          </div>

          <div v-if="paymentModal.error" class="pay-error">{{ paymentModal.error }}</div>
        </div>

        <div class="confirm-actions">
          <button class="confirm-btn-secondary" @click="paymentModal.open = false" :disabled="paymentModal.saving">Cancel</button>
          <button class="confirm-btn-primary confirm-btn-primary--green" @click="submitPayment"
                  :disabled="paymentModal.saving || !paymentModal.form.payment_date || !paymentModal.form.amount || !paymentModal.form.file">
            <span v-if="paymentModal.saving" class="spinner-sm"></span>
            {{ paymentModal.saving ? 'Recording…' : 'Record Payment' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reversal Confirm Modal -->
    <div v-if="reversalModal.open" class="confirm-overlay" @click.self="reversalModal.open = false">
      <div class="confirm-box confirm-box--sm">
        <div class="confirm-icon-wrap confirm-icon-wrap--red">
          <font-awesome-icon icon="undo" class="confirm-icon" />
        </div>
        <h4 class="confirm-title">Reverse Payment</h4>
        <p class="confirm-subtitle">Reverse {{ reversalModal.payment?.payment_number }} ({{ po.currency }} {{ formatNum(reversalModal.payment?.amount) }})?</p>
        <div class="supply-form-row" style="padding:0 0 12px">
          <label class="supply-form-label">Reason <span class="req">*</span></label>
          <input v-model="reversalModal.reason" class="supply-form-input" placeholder="Reason for reversal" />
        </div>
        <div class="confirm-actions">
          <button class="confirm-btn-secondary" @click="reversalModal.open = false" :disabled="reversalModal.saving">Cancel</button>
          <button class="confirm-btn-primary confirm-btn-primary--red" @click="submitReversal"
                  :disabled="reversalModal.saving || !reversalModal.reason.trim()">
            <span v-if="reversalModal.saving" class="spinner-sm"></span>
            {{ reversalModal.saving ? 'Reversing…' : 'Confirm Reversal' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Supply modal -->
    <transition name="modal-fade">
      <div v-if="supplyModal.open" class="confirm-overlay" @click.self="supplyModal.open = false">
        <div class="confirm-box supply-modal-box">
          <div class="confirm-icon-wrap confirm-icon-wrap--blue">
            <font-awesome-icon :icon="isGoodsPO ? 'boxes' : 'check-circle'" class="confirm-icon" />
          </div>

          <h4 class="confirm-title">
            <template v-if="isGoodsPO">{{ supplyModal.mode === 'all' ? 'Supply All Items' : 'Supply Item' }}</template>
            <template v-else>{{ supplyModal.mode === 'all' ? 'Mark All as Delivered' : 'Mark as Delivered' }}</template>
          </h4>
          <p class="confirm-subtitle">
            <template v-if="isGoodsPO">
              {{ supplyModal.mode === 'all' ? 'Record goods received for all pending items.' : `Record goods received for: ${supplyModal.line?.description}` }}
            </template>
            <template v-else>
              {{ supplyModal.mode === 'all' ? 'Mark all pending items as delivered.' : `Mark as delivered: ${supplyModal.line?.description}` }}
            </template>
          </p>

          <div class="supply-form">
            <!-- Goods: item mode -->
            <template v-if="isGoodsPO && supplyModal.mode === 'item'">
              <div class="supply-form-row">
                <label class="supply-form-label">Quantity to Supply <span class="req">*</span></label>
                <input v-model.number="supplyModal.quantity" type="number" min="0.0001" step="any"
                       class="supply-form-input" :placeholder="`Max: ${supplyRemaining(supplyModal.line)}`" />
                <span class="supply-form-hint">Remaining after supply: {{ Math.max(0, supplyRemaining(supplyModal.line) - (supplyModal.quantity || 0)) }}</span>
              </div>

              <div class="supply-form-row">
                <label class="supply-form-label">
                  Product <span class="req">*</span>
                  <span v-if="supplyModal.product_id" class="supply-pr-lock-tag">from PO</span>
                </label>
                <select v-model="supplyModal.product_id" class="supply-form-input supply-form-select"
                        :disabled="!!supplyModal.product_id"
                        :class="{ 'supply-select-locked': !!supplyModal.product_id }">
                  <option value="">Select product...</option>
                  <option v-for="p in products" :key="p.id" :value="p.id">{{ p.product_name }}</option>
                </select>
              </div>

              <div class="supply-form-row">
                <label class="supply-form-label">
                  Destination Location <span class="req">*</span>
                  <span v-if="supplyModal.location_id" class="supply-pr-lock-tag">
                    {{ supplyModal.line?.location_id ? 'from line' : (po.location_id ? 'from PO' : 'from PR') }}
                  </span>
                </label>
                <select v-model="supplyModal.location_id" class="supply-form-input supply-form-select"
                        :disabled="!!supplyModal.location_id"
                        :class="{ 'supply-select-locked': !!supplyModal.location_id }">
                  <option value="">Select location...</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.store_name }}</option>
                </select>
              </div>
            </template>

            <!-- Goods: all mode -->
            <template v-else-if="isGoodsPO && supplyModal.mode === 'all'">
              <p class="supply-all-note">
                <font-awesome-icon icon="info-circle" class="me-1" />
                Items without a product or location assigned will be skipped. Assign them individually using the Supply button.
              </p>
              <div class="supply-form-row">
                <label class="supply-form-label">
                  Destination Location
                  <span v-if="po.location_id || po.pr?.location_id" class="supply-pr-lock-tag">
                    {{ po.location_id ? 'from PO' : 'from PR' }}
                  </span>
                </label>
                <select v-model="supplyModal.location_id" class="supply-form-input supply-form-select"
                        :disabled="!!(po.location_id || po.pr?.location_id)"
                        :class="{ 'supply-select-locked': !!(po.location_id || po.pr?.location_id) }">
                  <option value="">No location</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.store_name }}</option>
                </select>
              </div>
            </template>

            <!-- Service / Expense: any mode — no form fields, just confirm -->
            <template v-else>
              <p class="supply-all-note supply-all-note--confirm">
                <font-awesome-icon icon="check-circle" class="me-1" />
                This will mark {{ supplyModal.mode === 'all' ? 'all pending items' : 'this item' }} as delivered. No inventory transaction will be recorded.
              </p>
            </template>
          </div>

          <div class="confirm-actions">
            <button class="confirm-btn-secondary" @click="supplyModal.open = false" :disabled="!!actioning">
              Cancel
            </button>
            <button class="confirm-btn-primary confirm-btn-primary--blue"
                    :disabled="!!actioning"
                    @click="doSupply">
              <span v-if="actioning === 'supply'" class="spinner-sm"></span>
              <font-awesome-icon v-else icon="check" class="me-1" />
              {{ actioning === 'supply' ? 'Recording…' : (isGoodsPO ? 'Confirm Supply' : 'Confirm Delivery') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Attach Document modal -->
    <transition name="modal-fade">
      <div v-if="attachModal.open" class="confirm-overlay" @click.self="closeAttachModal">
        <div class="confirm-box attach-modal-box">
          <h4 class="confirm-title">Attach Document</h4>
          <p class="confirm-subtitle">Upload a file and give it a name.</p>

          <div class="supply-form">
            <div class="supply-form-row">
              <label class="supply-form-label">Document Name <span class="req">*</span></label>
              <input v-model="attachModal.name" class="supply-form-input" placeholder="e.g. Vendor Invoice, Delivery Note" />
            </div>
            <div class="supply-form-row">
              <label class="supply-form-label">File <span class="req">*</span></label>
              <div class="file-pick-area" @click="$refs.fileInput.click()" @dragover.prevent @drop.prevent="onFileDrop">
                <font-awesome-icon icon="upload" class="file-pick-icon" />
                <span v-if="attachModal.file">{{ attachModal.file.name }} ({{ formatFileSize(attachModal.file.size) }})</span>
                <span v-else>Click or drag a file here<br><small>PDF, Word, Excel, image — max 20 MB</small></span>
              </div>
              <input ref="fileInput" type="file" style="display:none"
                     accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                     @change="onFileSelect" />
            </div>
          </div>

          <div class="confirm-actions">
            <button class="confirm-btn-secondary" @click="closeAttachModal" :disabled="attachModal.saving">Cancel</button>
            <button class="confirm-btn-primary confirm-btn-primary--green" @click="uploadDocument" :disabled="attachModal.saving || !attachModal.file || !attachModal.name.trim()">
              <span v-if="attachModal.saving" class="spinner-sm"></span>
              <font-awesome-icon v-else icon="paperclip" class="me-1" />
              {{ attachModal.saving ? 'Uploading…' : 'Attach' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirm modal -->
    <transition name="modal-fade">
      <div v-if="confirmModal.open" class="confirm-overlay" @click.self="confirmModal.open = false">
        <div class="confirm-box">
          <!-- Icon header -->
          <div class="confirm-icon-wrap" :class="confirmModal.type === 'issue' ? 'confirm-icon-wrap--green' : 'confirm-icon-wrap--red'">
            <font-awesome-icon :icon="confirmModal.type === 'issue' ? 'paper-plane' : (confirmModal.type === 'delete_doc' ? 'trash' : 'ban')" class="confirm-icon" />
          </div>

          <h4 class="confirm-title">{{ confirmModal.type === 'issue' ? 'Issue Purchase Order' : (confirmModal.type === 'delete_doc' ? 'Remove Document' : 'Cancel Purchase Order') }}</h4>
          <p class="confirm-subtitle">{{ confirmModal.type === 'issue' ? 'This order will be sent to the vendor.' : 'This action cannot be undone.' }}</p>

          <!-- PO summary card -->
          <div class="confirm-po-card" v-if="confirmModal.type !== 'delete_doc'">
            <div class="confirm-po-row">
              <span class="confirm-po-label">Order No.</span>
              <span class="confirm-po-mono">{{ po?.po_number }}</span>
            </div>
            <div class="confirm-po-row" v-if="po?.vendor?.name">
              <span class="confirm-po-label">Vendor</span>
              <span class="confirm-po-val">{{ po.vendor.name }}</span>
            </div>
            <div class="confirm-po-row" v-if="confirmModal.type === 'issue'">
              <span class="confirm-po-label">Order Total</span>
              <span class="confirm-po-total">{{ po?.currency }} {{ formatNum(po?.total) }}</span>
            </div>
            <div class="confirm-po-row" v-if="po?.expected_delivery_date && confirmModal.type === 'issue'">
              <span class="confirm-po-label">Expected Delivery</span>
              <span class="confirm-po-val">{{ formatDate(po.expected_delivery_date) }}</span>
            </div>
          </div>

          <p v-if="confirmModal.type === 'issue'" class="confirm-notice">
            <font-awesome-icon icon="info-circle" class="confirm-notice-icon" />
            Once issued, the order cannot be edited. Make sure all details are correct.
          </p>
          <p v-else class="confirm-notice confirm-notice--warn">
            <font-awesome-icon icon="exclamation-triangle" class="confirm-notice-icon" />
            Cancelling will release all reserved PR line quantities.
          </p>

          <div class="confirm-actions">
            <button class="confirm-btn-secondary" @click="confirmModal.open = false" :disabled="!!actioning">
              Go Back
            </button>
            <button
              class="confirm-btn-primary"
              :class="confirmModal.type === 'issue' ? 'confirm-btn-primary--green' : 'confirm-btn-primary--red'"
              :disabled="!!actioning"
              @click="confirmModal.onConfirm()">
              <span v-if="actioning" class="spinner-sm"></span>
              <font-awesome-icon v-else :icon="confirmModal.type === 'issue' ? 'paper-plane' : 'ban'" class="me-1" />
              {{ actioning ? (confirmModal.type === 'issue' ? 'Submitting…' : 'Cancelling…') : (confirmModal.type === 'issue' ? 'Yes, Issue Order' : 'Yes, Cancel Order') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
    </template>
  </div>
</template>

<script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';

  export default {
    setup() { return { toast: useToast() }; },
    data() {
      return {
        po:        null,
        loading:   false,
        actioning: false,
        confirmModal: { open: false, type: null, onConfirm: null },
        supplyModal:  { open: false, mode: null, line: null, quantity: null, product_id: '', location_id: '' },
        attachModal:  { open: false, name: '', file: null, saving: false },
        paymentData:  null,
        paymentsLoading: false,
        paymentModal: {
          open: false, saving: false, error: '',
          form: { payment_date: '', amount: '', payment_method: 'bank_transfer',
                  transaction_reference: '', payment_reference: '', notes: '',
                  file: null, document_name: '' },
        },
        reversalModal: { open: false, payment: null, reason: '', saving: false },
        products:  [],
        locations: [],
      };
    },
    computed: {
      isGoodsPO() {
        return (this.po?.item_type ?? 'goods') === 'goods';
      },
      canSupply() {
        return this.$store?.state?.getCurrentUserPermission?.supply_po_items;
      },
      canRecordPayment() {
        return this.$store?.state?.getCurrentUserPermission?.record_po_payment
            || this.$store?.state?.getCurrentUserPermission?.manage_purchase_orders;
      },
      hasUnsuppliedLines() {
        return this.po?.lines?.some(l => l.supply_status !== 'supplied') ?? false;
      },
      quantityLogs() {
        if (!this.po?.lines) return [];
        const logs = [];
        for (const line of this.po.lines) {
          for (const log of (line.quantity_logs ?? [])) {
            logs.push({ ...log, lineName: line.description, uom: line.uom });
          }
        }
        return logs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      },
    },
    created() { this.fetchPO(); this.loadSupplyDropdowns(); },
    watch: {
      po(val) {
        if (val && !['draft','cancelled'].includes(val.status)) {
          this.fetchPayments();
        }
      },
    },
    methods: {
      async fetchPO() {
        this.loading = true;
        try {
          const { data } = await axios.get(`/procurement/purchase-orders/${this.$route.params.id}`);
          this.po = data.data;
        } catch { this.toast.error('Failed to load purchase order'); }
        finally { this.loading = false; }
      },
      async loadSupplyDropdowns() {
        try {
          const [pRes, lRes] = await Promise.all([
            axios.get('/inventory/product/active/all'),
            axios.get('/admin/store/all'),
          ]);
          this.products  = pRes.data?.data ?? [];
          this.locations = lRes.data?.data ?? [];
        } catch { /* non-critical */ }
      },
      resolvedProduct(line) {
        if (line.product) return line.product;
        const prLine = this.po.pr?.lines?.find(l => l.id === line.pr_line_id);
        return prLine ? (this.products.find(p => p.id === prLine.procurement_product_id) || null) : null;
      },
      resolvedLocation(line) {
        if (line.location) return line.location;
        const locId = this.po.pr?.location_id;
        return locId ? (this.locations.find(l => l.id === locId) || null) : null;
      },
      supplyRemaining(line) {
        if (!line) return 0;
        return Math.max(0, parseFloat(line.quantity || 0) - parseFloat(line.supplied_quantity || 0));
      },
      supplyStatusLabel(s) {
        return { pending: 'Pending', partially_supplied: 'Partial', supplied: 'Supplied' }[s] ?? 'Pending';
      },
      openSupplyItemModal(line) {
        const prLine    = this.po.pr?.lines?.find(l => l.id === line.pr_line_id);
        const productId  = line.product_id  || prLine?.procurement_product_id || '';
        const locationId = line.location_id || this.po.location_id || this.po.pr?.location_id || '';
        this.supplyModal = {
          open: true, mode: 'item', line,
          quantity:    this.supplyRemaining(line),
          product_id:  productId,
          location_id: locationId,
        };
      },
      openSupplyAllModal() {
        this.supplyModal = { open: true, mode: 'all', line: null, quantity: null, product_id: '', location_id: this.po.location_id || this.po.pr?.location_id || '' };
      },
      async doSupply() {
        this.actioning = 'supply';
        try {
          if (this.supplyModal.mode === 'item') {
            const line = this.supplyModal.line;
            const payload = this.isGoodsPO
              ? {
                  supplied_quantity: this.supplyModal.quantity,
                  product_id:  this.supplyModal.product_id  || undefined,
                  location_id: this.supplyModal.location_id || undefined,
                }
              : { supplied_quantity: this.supplyRemaining(line) };
            const { data } = await axios.post(
              `/procurement/purchase-orders/${this.po.id}/lines/${line.id}/supply`, payload
            );
            this.po = data.data;
            this.toast.success(this.isGoodsPO ? 'Item supplied successfully' : 'Item marked as delivered');
          } else {
            const payload = {};
            if (this.isGoodsPO && this.supplyModal.location_id) payload.location_id = this.supplyModal.location_id;
            const { data } = await axios.post(`/procurement/purchase-orders/${this.po.id}/supply`, payload);
            this.po = data.data;
            this.toast.success(data.message || (this.isGoodsPO ? 'Items supplied' : 'Items marked as delivered'));
          }
          this.supplyModal.open = false;
        } catch (e) {
          this.toast.error(e.response?.data?.message || 'Failed to record supply');
        } finally {
          this.actioning = false;
        }
      },
      issuePO() {
        this.confirmModal = { open: true, type: 'issue', onConfirm: this.doIssue };
      },
      async doIssue() {
        this.actioning = 'issue';
        try {
          const { data } = await axios.post(`/procurement/purchase-orders/${this.po.id}/issue`);
          this.confirmModal.open = false;
          this.toast.success(data.message || 'Purchase order issued');
          await this.fetchPO();
        } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to issue'); }
        finally { this.actioning = false; }
      },
      cancelPO() {
        this.confirmModal = { open: true, type: 'cancel', onConfirm: this.doCancel };
      },
      async doCancel() {
        this.actioning = 'cancel';
        try {
          await axios.post(`/procurement/purchase-orders/${this.po.id}/cancel`);
          this.confirmModal.open = false;
          this.toast.success('Purchase order cancelled');
          await this.fetchPO();
        } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to cancel'); }
        finally { this.actioning = false; }
      },
      closeAttachModal() {
        this.attachModal = { open: false, name: '', file: null, saving: false };
      },
      onFileSelect(e) {
        this.attachModal.file = e.target.files[0] || null;
      },
      onFileDrop(e) {
        this.attachModal.file = e.dataTransfer.files[0] || null;
      },
      async uploadDocument() {
        if (!this.attachModal.file || !this.attachModal.name.trim()) return;
        this.attachModal.saving = true;
        try {
          const fd = new FormData();
          fd.append('document_name', this.attachModal.name.trim());
          fd.append('file', this.attachModal.file);
          const { data } = await axios.post(`/procurement/purchase-orders/${this.po.id}/documents`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          this.po.documents = [...(this.po.documents || []), data.data];
          this.toast.success('Document attached');
          this.closeAttachModal();
        } catch (e) {
          this.toast.error(e.response?.data?.message || 'Upload failed');
        } finally {
          this.attachModal.saving = false;
        }
      },
      confirmDeleteDoc(doc) {
        this.confirmModal = {
          open: true, type: 'delete_doc',
          onConfirm: () => this.deleteDocument(doc),
        };
      },
      async deleteDocument(doc) {
        this.actioning = 'delete_doc';
        try {
          await axios.delete(`/procurement/purchase-orders/${this.po.id}/documents/${doc.id}`);
          this.po.documents = this.po.documents.filter(d => d.id !== doc.id);
          this.confirmModal.open = false;
          this.toast.success('Document removed');
        } catch (e) {
          this.toast.error(e.response?.data?.message || 'Failed to delete document');
        } finally {
          this.actioning = false;
        }
      },
      docIcon(fileName) {
        const ext = (fileName || '').split('.').pop().toLowerCase();
        if (['jpg','jpeg','png','gif','webp'].includes(ext)) return 'image';
        if (ext === 'pdf') return 'file-pdf';
        if (['doc','docx'].includes(ext)) return 'file-word';
        if (['xls','xlsx','csv'].includes(ext)) return 'file-excel';
        return 'file-alt';
      },
      formatFileSize(bytes) {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
      },
      // ── Payment methods ────────────────────────────────────────────
      async fetchPayments() {
        this.paymentsLoading = true;
        try {
          const { data } = await axios.get(`/procurement/purchase-orders/${this.$route.params.id}/payments`);
          this.paymentData = data.data;
        } catch { /* non-critical */ }
        finally { this.paymentsLoading = false; }
      },

      openPaymentModal() {
        const outstanding = this.paymentData?.po?.outstanding ?? this.po?.total ?? 0;
        this.paymentModal.form = {
          payment_date: new Date().toISOString().split('T')[0],
          amount: outstanding,
          payment_method: 'bank_transfer',
          transaction_reference: '', payment_reference: '', notes: '',
          file: null, document_name: '',
        };
        this.paymentModal.error = '';
        this.paymentModal.open = true;
      },

      onPayFileSelect(e) {
        const file = e.target.files[0];
        if (file) {
          this.paymentModal.form.file = file;
          if (!this.paymentModal.form.document_name) this.paymentModal.form.document_name = file.name.replace(/\.[^.]+$/, '');
        }
      },

      onPayFileDrop(e) {
        const file = e.dataTransfer.files[0];
        if (file) {
          this.paymentModal.form.file = file;
          if (!this.paymentModal.form.document_name) this.paymentModal.form.document_name = file.name.replace(/\.[^.]+$/, '');
        }
      },

      async submitPayment() {
        this.paymentModal.error = '';
        if (!this.paymentModal.form.amount || this.paymentModal.form.amount <= 0) {
          this.paymentModal.error = 'Please enter a valid amount.'; return;
        }
        if (!this.paymentModal.form.file) {
          this.paymentModal.error = 'Payment receipt is required.'; return;
        }
        this.paymentModal.saving = true;
        try {
          const fd = new FormData();
          const f  = this.paymentModal.form;
          fd.append('payment_date', f.payment_date);
          fd.append('amount', f.amount);
          fd.append('payment_method', f.payment_method);
          if (f.transaction_reference) fd.append('transaction_reference', f.transaction_reference);
          if (f.payment_reference) fd.append('payment_reference', f.payment_reference);
          if (f.notes) fd.append('notes', f.notes);
          if (f.file) { fd.append('file', f.file); fd.append('document_name', f.document_name || f.file.name); }

          await axios.post(`/procurement/purchase-orders/${this.$route.params.id}/payments`, fd,
            { headers: { 'Content-Type': 'multipart/form-data' } });

          this.toast.success('Payment recorded successfully');
          this.paymentModal.open = false;
          await this.fetchPO();
          await this.fetchPayments();
        } catch (e) {
          this.paymentModal.error = e.response?.data?.message || 'Failed to record payment';
        } finally { this.paymentModal.saving = false; }
      },

      confirmReversePayment(payment) {
        this.reversalModal = { open: true, payment, reason: '', saving: false };
      },

      async submitReversal() {
        this.reversalModal.saving = true;
        try {
          await axios.post(`/procurement/payments/${this.reversalModal.payment.id}/reverse`,
            { reversal_reason: this.reversalModal.reason });
          this.toast.success('Payment reversed');
          this.reversalModal.open = false;
          await this.fetchPO();
          await this.fetchPayments();
        } catch (e) {
          this.toast.error(e.response?.data?.message || 'Failed to reverse payment');
        } finally { this.reversalModal.saving = false; }
      },

      formatStatus(s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
      formatApprovalStatus(s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
      capitalise(s) { return (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); },
      formatDate(d)   { return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }); },
      formatNum(v) {
        const n = parseFloat(v);
        const amount = isNaN(n) ? 0 : n;
        const [int, dec] = amount.toFixed(2).split('.');
        return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
      },
    },
  };
</script>

<style scoped>
.back-row   { margin-bottom:14px; }
.btn-back   { background:transparent; border:none; color:#7a90b0; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; padding:0; }
.btn-back:hover { color:#004AAD; }
.loading-state { display:flex; flex-direction:column; align-items:center; padding:60px; color:#7a90b0; gap:10px; }
.spinner    { width:32px; height:32px; border:3px solid #e8edf5; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
.page-header   { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:20px; gap:12px; flex-wrap:wrap; }
.title-row     { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:6px; }
.page-title    { font-size:20px; font-weight:700; color:#1a2b45; margin:0; }
.page-sub      { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:4px; }
.mono          { font-family:monospace; font-size:12.5px; background:#f4f7fb; padding:3px 8px; border-radius:5px; color:#5a6a85; }
.mono-inline   { font-family:monospace; font-size:12.5px; color:#004AAD; }
.header-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.btn-outline   { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-issue     { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; }
.btn-issue:hover:not(:disabled)  { background:#15803d; }
.btn-issue:disabled  { opacity:.5; cursor:not-allowed; }
.btn-cancel    { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#c2410c; border:1.5px solid #fed7aa; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; }
.btn-cancel:hover:not(:disabled) { background:#fff7ed; }
.btn-cancel:disabled { opacity:.5; cursor:not-allowed; }
.spinner-sm    { width:13px; height:13px; border:2px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; }
.status-badge  { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.status-badge--draft              { background:#f1f5ff; color:#004AAD; }
.status-badge--issued             { background:#fef9c3; color:#854d0e; }
.status-badge--partially_received { background:#fef3c7; color:#92400e; }
.status-badge--completed          { background:#dcfce7; color:#166534; }
.status-badge--cancelled          { background:#fee2e2; color:#991b1b; }
.status-badge--pending_approval   { background:#fff7ed; color:#c2410c; }

/* Meta */
.meta-grid  { display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; margin-bottom:16px; }
.meta-card  { background:#fff; border:1.5px solid #e8edf5; border-radius:10px; padding:14px 16px; display:flex; flex-direction:column; gap:4px; }
.meta-label { font-size:11.5px; font-weight:600; color:#7a90b0; text-transform:uppercase; letter-spacing:.5px; }
.meta-value { font-size:13.5px; font-weight:600; color:#1a2b45; }

/* Sections */
.section-card  { background:#fff; border:1.5px solid #e8edf5; border-radius:12px; padding:20px 22px; margin-bottom:14px; }
.section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 14px; }
.table-wrap    { overflow-x:auto; }
.detail-table  { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; }
.detail-table th { background:#f4f7fb; font-weight:600; color:#5a6a85; padding:10px 12px; text-align:left; font-size:12px; white-space:nowrap; }
.detail-table td { padding:10px 12px; border-top:1px solid #f0f4fa; color:#1a2b45; vertical-align:middle; }
.detail-table tbody tr:nth-child(even) { background:#fafbff; }
.num-col                  { text-align:right; font-variant-numeric:tabular-nums; }
.detail-table th.num-col  { text-align:right; }
.fw-bold    { font-weight:700; }
.text-muted { color:#7a90b0; }
.tfoot-sub  { background:#f4f7fb; }
.tfoot-total{ background:#eef4ff; }
.tfoot-label{ text-align:right; font-size:12.5px; font-weight:600; color:#5a6a85; }
.total-val  { color:#004AAD; font-size:14px; }
.notes-text { font-size:13.5px; color:#1a2b45; line-height:1.6; margin:0 0 8px; }
.rich-text p { margin:0 0 4px; } .rich-text ul,.rich-text ol { margin:4px 0 4px 18px; } .rich-text :last-child { margin-bottom:0; }
.text-muted-sm { font-size:13px; color:#7a90b0; margin:0; }

/* Supply section */
.supply-header       { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.btn-supply-all      { display:inline-flex; align-items:center; gap:6px; background:#0f766e; color:#fff; font-size:12.5px; font-weight:600; padding:7px 16px; border-radius:8px; border:none; cursor:pointer; }
.btn-supply-all:hover:not(:disabled) { background:#0d5c57; }
.btn-supply-all:disabled { opacity:.5; cursor:not-allowed; }
.btn-supply-item     { background:#f0fdfa; color:#0f766e; border:1.5px solid #99f6e4; font-size:12px; font-weight:700; padding:3px 10px; border-radius:6px; cursor:pointer; white-space:nowrap; }
.btn-supply-item:hover { background:#ccfbf1; }
.supply-table-wrap   { overflow-x:auto; }
.supply-table        { width:100%; border-collapse:collapse; font-size:13px; }
.supply-table th     { background:#f0fdfa; font-weight:600; color:#5a6a85; padding:10px 12px; text-align:left; font-size:12px; white-space:nowrap; }
.supply-table td     { padding:10px 12px; border-top:1px solid #f0f4fa; color:#1a2b45; vertical-align:middle; }
.supply-product-tag  { display:inline-block; background:#eff6ff; color:#1d4ed8; font-size:11.5px; font-weight:600; padding:2px 8px; border-radius:20px; }
.supply-location-tag { display:inline-flex; align-items:center; gap:4px; background:#f0fdf4; color:#166534; font-size:11.5px; font-weight:600; padding:2px 8px; border-radius:20px; }
.supply-supplied-qty { color:#0f766e; font-weight:700; }
.supply-remaining-warn { color:#92400e; font-weight:700; }
.supply-badge        { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:2px 9px; text-transform:capitalize; }
.supply-badge--pending           { background:#f1f5ff; color:#004AAD; }
.supply-badge--partially_supplied{ background:#fef3c7; color:#92400e; }
.supply-badge--supplied          { background:#dcfce7; color:#166534; }

/* Supply modal */
.supply-modal-box    { max-width:460px; }
.confirm-icon-wrap--blue { background:linear-gradient(135deg,#dbeafe,#bfdbfe); box-shadow:0 6px 20px rgba(37,99,235,.2); }
.confirm-icon-wrap--blue .confirm-icon { color:#1d4ed8; }
.supply-form         { width:100%; display:flex; flex-direction:column; gap:12px; margin-bottom:20px; }
.supply-form-row     { display:flex; flex-direction:column; gap:4px; }
.supply-form-label   { font-size:12.5px; font-weight:600; color:#5a6a85; }
.supply-form-label small { font-weight:400; color:#9aa6b9; }
.supply-form-input   { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; box-sizing:border-box; }
.supply-form-input:focus { border-color:#004AAD; }
.supply-form-hint    { font-size:12px; color:#7a90b0; }
.supply-form-select  { appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%235a6a85'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; background-size:16px; padding-right:32px; }
.supply-info-row     { display:flex; align-items:center; justify-content:space-between; padding:8px 12px; background:#f8fafc; border-radius:8px; }
.supply-info-label   { font-size:12px; font-weight:600; color:#94a3b8; }
.supply-info-val     { font-size:13px; font-weight:600; color:#1a2b45; }
.supply-all-note     { font-size:12.5px; color:#92400e; background:#fff7ed; border:1px solid #fed7aa; border-radius:8px; padding:10px 12px; margin:0; line-height:1.5; }
.supply-all-note--confirm { color:#166534; background:#f0fdf4; border-color:#bbf7d0; }
.confirm-btn-primary--blue { background:linear-gradient(135deg,#1d4ed8,#1e40af); box-shadow:0 4px 12px rgba(29,78,216,.3); }
.confirm-btn-primary--blue:hover:not(:disabled) { background:linear-gradient(135deg,#1e40af,#1e3a8a); }

/* Approval section */
.approval-status-row { display:flex; align-items:center; gap:10px; margin-bottom:16px; }
.appr-badge { display:inline-block; font-size:11px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.appr-badge--in_progress { background:#fff7ed; color:#c2410c; }
.appr-badge--approved    { background:#dcfce7; color:#166534; }
.appr-badge--rejected    { background:#fee2e2; color:#991b1b; }
.appr-badge--cancelled   { background:#f1f5f9; color:#64748b; }
.appr-step { font-size:12px; color:#7a90b0; }
.approval-steps { display:flex; flex-direction:column; gap:14px; }
.appr-step-row  { display:flex; align-items:flex-start; gap:12px; }
.appr-dot { width:10px; height:10px; border-radius:50%; background:#dde5f0; flex-shrink:0; margin-top:5px; }
.appr-dot--approved   { background:#22c55e; }
.appr-dot--rejected   { background:#ef4444; }
.appr-dot--in_progress{ background:#f97316; }
.appr-dot--pending    { background:#dde5f0; }
.appr-step-info       { flex:1; }
.appr-step-name       { font-size:13px; font-weight:600; color:#1a2b45; }
.appr-step-meta       { margin-top:2px; font-size:12px; }
.appr-step-status     { display:inline-block; font-size:11px; font-weight:600; border-radius:12px; padding:1px 8px; }
.appr-step-status--approved   { background:#dcfce7; color:#166534; }
.appr-step-status--rejected   { background:#fee2e2; color:#991b1b; }
.appr-step-status--in_progress{ background:#fff7ed; color:#c2410c; }
.appr-step-status--pending    { background:#f1f5f9; color:#64748b; }
.appr-step-date    { color:#7a90b0; margin-left:4px; }
.appr-step-comment { font-size:12px; color:#5a6a85; margin:4px 0 0; font-style:italic; }

/* Quantity log */
.qlog-list   { display:flex; flex-direction:column; gap:10px; }
.qlog-row    { display:flex; align-items:flex-start; gap:12px; padding:11px 14px; background:#f8faff; border:1.5px solid #e8edf5; border-radius:10px; }
.qlog-icon   { width:30px; height:30px; border-radius:8px; background:#eff6ff; color:#2563eb; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; margin-top:1px; }
.qlog-body   { flex:1; min-width:0; }
.qlog-desc   { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:4px; }
.qlog-line-name { font-size:13px; font-weight:600; color:#1a2b45; }
.qlog-change { display:inline-flex; align-items:center; gap:6px; }
.qlog-old    { font-size:12.5px; font-weight:600; color:#991b1b; background:#fee2e2; padding:1px 7px; border-radius:5px; font-variant-numeric:tabular-nums; text-decoration:line-through; }
.qlog-new    { font-size:12.5px; font-weight:700; color:#166534; background:#dcfce7; padding:1px 7px; border-radius:5px; font-variant-numeric:tabular-nums; }
.qlog-arrow  { font-size:10px; color:#7a90b0; }
.qlog-uom    { font-size:11.5px; color:#7a90b0; }
.qlog-meta   { font-size:12px; color:#7a90b0; display:flex; align-items:center; flex-wrap:wrap; gap:2px; }
.qlog-by     { font-weight:600; color:#5a6a85; }
.qlog-desig  { color:#7a90b0; }
.qlog-sep    { color:#d0d8e8; }
.qlog-step   { color:#7a90b0; }
.qlog-date   { color:#7a90b0; }

@media (max-width: 768px) {
  .meta-grid { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 576px) {
  .meta-grid    { grid-template-columns: 1fr; }
  .section-card { padding: 14px 12px; }

  /* drop index, Specification and UOM columns — tfoot excluded intentionally */
  .detail-table thead th:nth-child(1),
  .detail-table tbody td:nth-child(1),
  .detail-table thead th:nth-child(3),
  .detail-table tbody td:nth-child(3),
  .detail-table thead th:nth-child(5),
  .detail-table tbody td:nth-child(5) { display: none; }

  .detail-table          { table-layout: auto; font-size: 12px; }
  .detail-table th,
  .detail-table td       { padding: 8px 8px; white-space: normal; }
  .detail-table th       { white-space: nowrap; }
}

/* ── Confirm modal ───────────────────────────────────────────────── */
.confirm-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(10, 20, 40, .55);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.confirm-box {
  background: #fff;
  border-radius: 20px;
  width: 100%; max-width: 420px;
  padding: 32px 28px 28px;
  display: flex; flex-direction: column; align-items: center;
  gap: 0;
  box-shadow: 0 24px 60px rgba(0,0,0,.18), 0 4px 16px rgba(0,0,0,.08);
}

/* icon */
.confirm-icon-wrap {
  width: 64px; height: 64px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 18px;
}
.confirm-icon-wrap--green { background: linear-gradient(135deg, #dcfce7, #bbf7d0); box-shadow: 0 6px 20px rgba(22,163,74,.2); }
.confirm-icon-wrap--red   { background: linear-gradient(135deg, #fee2e2, #fecaca); box-shadow: 0 6px 20px rgba(220,38,38,.2); }
.confirm-icon      { font-size: 26px; }
.confirm-icon-wrap--green .confirm-icon { color: #16a34a; }
.confirm-icon-wrap--red   .confirm-icon { color: #dc2626; }

/* text */
.confirm-title    { font-size: 18px; font-weight: 800; color: #0f172a; margin: 0 0 6px; text-align: center; }
.confirm-subtitle { font-size: 13px; color: #64748b; margin: 0 0 20px; text-align: center; }

/* PO summary card */
.confirm-po-card {
  width: 100%;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  display: flex; flex-direction: column; gap: 10px;
}
.confirm-po-row   { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.confirm-po-label { font-size: 11.5px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; white-space: nowrap; }
.confirm-po-mono  { font-family: monospace; font-size: 13px; font-weight: 700; color: #004AAD; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; padding: 2px 8px; }
.confirm-po-val   { font-size: 13.5px; font-weight: 600; color: #1a2b45; text-align: right; }
.confirm-po-total { font-size: 15px; font-weight: 800; color: #16a34a; text-align: right; font-variant-numeric: tabular-nums; }

/* notice */
.confirm-notice {
  width: 100%;
  font-size: 12.5px; color: #475569;
  background: #f1f5f9; border: 1px solid #e2e8f0;
  border-radius: 8px; padding: 10px 12px;
  display: flex; align-items: flex-start; gap: 8px;
  margin-bottom: 24px; line-height: 1.5;
}
.confirm-notice--warn { background: #fff7ed; border-color: #fed7aa; color: #92400e; }
.confirm-notice-icon  { flex-shrink: 0; margin-top: 1px; }

/* buttons */
.confirm-actions { display: flex; gap: 10px; width: 100%; }
.confirm-btn-secondary {
  flex: 1; padding: 11px; border-radius: 10px;
  background: #f1f5f9; border: none; color: #475569;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
}
.confirm-btn-secondary:hover:not(:disabled) { background: #e2e8f0; }
.confirm-btn-primary {
  flex: 2; padding: 11px; border-radius: 10px;
  border: none; color: #fff;
  font-size: 13.5px; font-weight: 700; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
}
.confirm-btn-primary--green { background: linear-gradient(135deg, #16a34a, #15803d); box-shadow: 0 4px 12px rgba(22,163,74,.3); }
.confirm-btn-primary--green:hover:not(:disabled) { background: linear-gradient(135deg, #15803d, #14532d); }
.confirm-btn-primary--red   { background: linear-gradient(135deg, #dc2626, #b91c1c); box-shadow: 0 4px 12px rgba(220,38,38,.3); }
.confirm-btn-primary--red:hover:not(:disabled)   { background: linear-gradient(135deg, #b91c1c, #991b1b); }
.confirm-btn-primary:disabled,
.confirm-btn-secondary:disabled { opacity: .55; cursor: not-allowed; }

/* transition */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity .18s ease; }
.modal-fade-enter-active .confirm-box, .modal-fade-leave-active .confirm-box { transition: transform .18s ease, opacity .18s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .confirm-box, .modal-fade-leave-to .confirm-box { transform: scale(.96) translateY(8px); opacity: 0; }

/* ── Supply-all locked location ─────────────────────────────────── */
.supply-select-locked { background:#f4f7fb; color:#5a6a85; cursor:not-allowed; opacity:1; }
.supply-pr-lock-tag   { display:inline-block; font-size:10px; font-weight:700; background:#e0f2fe; color:#0369a1; border-radius:10px; padding:1px 7px; margin-left:6px; vertical-align:middle; text-transform:uppercase; letter-spacing:.4px; }

/* ── GRN section ─────────────────────────────────────────────────── */
.grn-number-badge { display:inline-block; font-family:monospace; font-size:12px; font-weight:700; background:#f0fdf4; color:#166534; border:1px solid #bbf7d0; border-radius:6px; padding:2px 8px; }
.grn-action-col { width:36px; text-align:center; }
.grn-print-btn { display:inline-flex; align-items:center; justify-content:center; width:28px; height:28px; border-radius:6px; border:1.5px solid #bbf7d0; background:#f0fdf4; color:#166534; cursor:pointer; font-size:12px; transition:background .15s, border-color .15s; }
.grn-print-btn:hover { background:#dcfce7; border-color:#86efac; }
.status-badge--completed { background:#dcfce7; color:#166534; }

/* ── Attach button ───────────────────────────────────────────────── */
.btn-attach { display:inline-flex; align-items:center; gap:6px; background:#f5f3ff; color:#6d28d9; border:1.5px solid #ddd6fe; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; }
.btn-attach:hover { background:#ede9fe; }

/* ── Documents list ─────────────────────────────────────────────── */
.doc-list { display:flex; flex-direction:column; gap:10px; }
.doc-row  { display:flex; align-items:center; gap:14px; padding:10px 14px; background:#f8fafc; border:1.5px solid #e8edf5; border-radius:9px; }
.doc-icon { width:34px; height:34px; background:#ede9fe; color:#6d28d9; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.doc-info { flex:1; min-width:0; }
.doc-name { font-size:13.5px; font-weight:600; color:#1a2b45; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.doc-meta { font-size:12px; color:#7a90b0; margin-top:2px; }
.doc-actions { display:flex; align-items:center; gap:8px; flex-shrink:0; }
.btn-doc-view   { display:inline-flex; align-items:center; gap:5px; font-size:12.5px; font-weight:600; color:#004AAD; background:#eff6ff; border:1.5px solid #bfdbfe; border-radius:6px; padding:5px 12px; text-decoration:none; cursor:pointer; }
.btn-doc-view:hover { background:#dbeafe; }
.btn-doc-delete { display:inline-flex; align-items:center; background:none; border:none; color:#dc2626; font-size:14px; cursor:pointer; padding:5px 8px; border-radius:6px; }
.btn-doc-delete:hover { background:#fee2e2; }
.btn-doc-delete:disabled { opacity:.4; cursor:not-allowed; }

/* ── Payment section ────────────────────────────────────────────── */
.section-title-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.btn-pay-record { background:#004AAD; color:#fff; border:none; border-radius:8px; padding:7px 16px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-pay-record:hover { background:#0057cc; }

.pay-summary-bar { display:flex; gap:24px; background:#f8fafc; border:1.5px solid #e4eaf2; border-radius:10px; padding:12px 18px; margin-bottom:16px; flex-wrap:wrap; }
.pay-sum-item { display:flex; flex-direction:column; gap:2px; }
.pay-sum-label { font-size:11.5px; color:#7a90b0; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.pay-sum-val { font-size:15px; font-weight:700; color:#1a2b45; }
.pay-sum-paid { color:#16a34a; }
.pay-sum-outstanding { color:#d97706; }
.pay-sub-heading { font-size:12.5px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.05em; margin:0 0 8px; }
.pay-loading { color:#7a90b0; font-size:13px; padding:8px 0; }
.pay-empty { color:#7a90b0; font-size:13px; padding:8px 0; }

.sched-status { display:inline-block; border-radius:20px; padding:2px 10px; font-size:11.5px; font-weight:700; text-transform:capitalize; }
.sched-pending { background:#f1f5f9; color:#64748b; }
.sched-due     { background:#fef3c7; color:#92400e; }
.sched-partially_paid { background:#dbeafe; color:#1d4ed8; }
.sched-paid    { background:#dcfce7; color:#166534; }
.sched-overdue { background:#fee2e2; color:#991b1b; }
.sched-cancelled { background:#f1f5f9; color:#94a3b8; }
.sched-completed { background:#dcfce7; color:#166534; }
.sched-reversed { background:#fce7f3; color:#9d174d; }

.receipt-row { display:inline-flex; align-items:center; gap:4px; margin-bottom:2px; }
.pay-receipt-link { display:inline-flex; align-items:center; gap:4px; font-size:12px; color:#004AAD; text-decoration:none; }
.pay-receipt-link:hover { text-decoration:underline; }
.pay-receipt-download { display:inline-flex; align-items:center; padding:2px 5px; border-radius:4px; color:#7a90b0; font-size:11px; text-decoration:none; }
.pay-receipt-download:hover { color:#004AAD; background:#eff6ff; }

/* Payment status chips */
.status-pay-unpaid        { background:#f1f5f9; color:#64748b; border-color:#cbd5e1; }
.status-pay-partially_paid{ background:#dbeafe; color:#1d4ed8; border-color:#93c5fd; }
.status-pay-fully_paid    { background:#dcfce7; color:#166534; border-color:#86efac; }
.status-pay-overpaid      { background:#fce7f3; color:#9d174d; border-color:#f9a8d4; }

/* Record payment modal */
.pay-modal-box { max-width:560px; width:100%; }
.pay-modal-body { padding:6px 0 16px; }
.pay-modal-summary { background:#f8fafc; border-radius:8px; padding:10px 14px; margin-bottom:14px; font-size:13.5px; color:#1a2b45; }
.field-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.field-span-2 { grid-column: 1/-1; }
.pay-error { color:#dc2626; font-size:12.5px; margin-top:8px; }
.confirm-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }

.doc-drop-zone { border:2px dashed #bfdbfe; border-radius:9px; padding:18px 14px; text-align:center; cursor:pointer; color:#7a90b0; font-size:13px; background:#f0f9ff; transition:background .15s, border-color .15s; display:flex; flex-direction:column; align-items:center; gap:5px; }
.doc-drop-zone:hover { background:#e0f2fe; border-color:#60a5fa; }
.doc-drop-zone--filled { flex-direction:row; justify-content:flex-start; padding:10px 14px; border-color:#16a34a; background:#f0fdf4; }

/* ── Attach modal ───────────────────────────────────────────────── */
.attach-modal-box { max-width:480px; }
.file-pick-area { border:2px dashed #c4b5fd; border-radius:9px; padding:24px 16px; text-align:center; cursor:pointer; color:#7a90b0; font-size:13px; line-height:1.6; background:#faf5ff; transition:background .15s; }
.file-pick-area:hover { background:#f5f3ff; border-color:#a78bfa; }
.file-pick-icon { font-size:22px; color:#a78bfa; display:block; margin-bottom:8px; }
</style>
